<template>
  <RfSimpleCard>
    <div class="system-app-content">
      <RfInput
        :modelValue="model.id"
        disabled
        variant="outlined"
        title="ID"
        hintMessage="Application unique identification code."
      >
        <template #append>
          <button
            type="button"
            class="relative inline-flex shrink-0 place-items-center before:absolute before:-inset-3 before:cursor-pointer"
            @click="copyToClipboard(model.id, 'Application ID copied to clipboard.')"
          >
            <RfCopyIcon class="h-6! w-6!" />
          </button>
        </template>
      </RfInput>
      <RfInput
        :modelValue="model.api_key"
        disabled
        variant="outlined"
        title="API Key"
        hintMessage="Application api key."
      >
        <template #append>
          <span class="inline-flex items-center gap-4">
            <button
              type="button"
              class="relative inline-flex shrink-0 place-items-center before:absolute before:-inset-3 before:cursor-pointer"
              @click="copyToClipboard(model.api_key, 'Api Key copied to clipboard.')"
            >
              <RfCopyIcon class="h-6! w-6!" />
            </button>
            <button
              type="button"
              class="relative inline-flex shrink-0 place-items-center before:absolute before:-inset-3 before:cursor-pointer"
              :disabled="userStore.isDisabledWithMessage"
              @click="showApiKeyDialog"
            >
              <RfResetIcon class="fill-beige-50 h-6! w-6!" />
            </button>
          </span>
        </template>
      </RfInput>
      <RfInput
        v-bind="formSystem.props.name.value.value"
        v-model="formSystem.values.name.value"
        :errorMessage="formSystem.errors.value?.name"
        :disabled="userStore.isReadOnly"
        requiredTitle
        title="Name"
        variant="outlined"
        hintMessage="Specify the name of your app or website."
      />
      <RfInput
        requiredTitle
        title="Domain"
        variant="outlined"
        hintMessage="Specify the domain of your customer facing application."
        v-bind="{
          ...(currApp.flags?.sandbox && !userStore.isCurrentSuperAdmin
            ? { disabled: true, modelValue: 'example.com' }
            : {
                ...formSystem.props.domain.value.value,
                'modelValue': formSystem.values.domain.value,
                'onUpdate:modelValue': value => (formSystem.values.domain.value = value),
                'errorMessage': formSystem.errors.value?.domain,
                'disabled': userStore.isReadOnly,
              }),
        }"
      />
      <RfInput
        title="Domain Aliases"
        variant="outlined"
        hintMessage="Specify additional domains. Do not include http(s)://"
        v-bind="{
          ...(currApp.flags?.sandbox && !userStore.isCurrentSuperAdmin
            ? { disabled: true, modelValue: 'example1.com, example2.com' }
            : {
                ...formSystem.props.domainList.value.value,
                'modelValue': (formSystem.values.domainList.value || []).join(', '),
                'errorMessage': formSystem.errors.value?.domainList,
                'disabled': userStore.isReadOnly,
                'onUpdate:modelValue': value =>
                  (formSystem.values.domainList.value = value.split(',').map(item => item.trim())),
              }),
        }"
      />
      <RfSelect
        v-model="timezone_offset"
        :items="timezoneOptions"
        title="Timezone"
        variant="outlined"
        sameWidth
        search
        :disabled="userStore.isReadOnly"
        hintMessage="Timezone where the app business is based."
      />
      <RfSelect
        v-model="composite_img_mimetype"
        :items="compositeImageOptions"
        title="Prompt Preview Format"
        variant="outlined"
        sameWidth
        :disabled="userStore.isReadOnly"
        hintMessage="Image format for prompts."
      />
      <RfSelect
        v-model="composite_img_quality"
        :items="compositeImageQualityOptions"
        title="Prompt Preview Quality"
        variant="outlined"
        sameWidth
        :disabled="
          userStore.isReadOnly ||
          (composite_img_mimetype === 'image/png' && 'Available only for JPEG and WebP')
        "
        hintMessage="Image quality for prompts."
      />
      <RfInput
        v-model.trim="session_timeout"
        type="number"
        variant="outlined"
        title="User Session Duration in Seconds"
      />
    </div>

    <p v-if="pinpointEnabled" class="flex items-center px-8 pt-2">
      Sender Email: {{ fromEmail }}
      <RfButton
        icon="md:edit"
        variant="plain"
        density="compact"
        class="ml-1"
        iconSize="16"
        color="accent"
        :disabled="userStore.isDisabledExtendedWithMessage"
        @click="showEmailDialog"
      />
    </p>

    <v-dialog v-model="showApiDialog" width="640" persistent>
      <v-card class="rf-custom-web-card-wrapper">
        <v-card-title>Regenerate API Key?</v-card-title>
        <v-card-text>
          Are you sure you want to regenerate the API Key? <br />
          Key <b>{{ model.api_key }}</b> will be invalidated.
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn size="large" class="cancel-btn" variant="outlined" @click="closeApiKeyDialog"
            >Cancel</v-btn
          >
          <v-btn
            size="large"
            style="width: 200px"
            variant="flat"
            color="success"
            @click="regenerateApiKey"
            >Regenerate Key</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="userStore.isAdmin" v-model="emailDialog" width="500" persistent>
      <v-card>
        <v-card-title>Verify Email</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.trim="fromEmail"
            density="compact"
            persistentPlaceholder
            variant="outlined"
            persistentHint
            label="Verify Email"
            hint="For email prompts, specify the sender email address"
          />
        </v-card-text>
        <v-card-actions class="modal-card-actions-div">
          <v-btn
            size="large"
            style="width: 100px"
            class="cancel-btn"
            variant="outlined"
            @click="closeEmailDialog"
            >Cancel</v-btn
          >
          <v-btn
            size="large"
            style="width: 150px"
            variant="flat"
            color="success"
            :disabled="emailSubmitDisabled"
            @click="submitEmail"
            >Submit</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </RfSimpleCard>
</template>

<script>
import RfSimpleCard from "@/components/RfSimpleCard.vue";
import RfButton from "@/components/buttons/RfButton.vue";
import copyToClipboard from "@/utils/CopyToClipboard";
import { useToastsStore } from "@/pinia/toastsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import RfInput from "@/components/inputs/RfInput.vue";
import RfCopyIcon from "@/components/icons/RfCopyIcon.vue";
import RfResetIcon from "@/components/icons/RfResetIcon.vue";
import { useFormSystem } from "@/utils/composables/useFormsValidation";
import RfSelect from "@/components/inputs/RfSelect.vue";
import { watch } from "vue";

export default {
  name: "RfSystem",
  components: {
    RfSimpleCard,
    RfButton,
    RfInput,
    RfCopyIcon,
    RfResetIcon,
    RfSelect,
  },
  props: ["model"],
  emits: ["update:formValid"],
  setup: (props, { emit }) => {
    const appsStore = useAppsStore();
    const formSystem = useFormSystem(
      {
        name: props.model.name,
        domain: props.model.domain,
        domainList: props.model.domain_aliases,
      },
      appsStore.app.flags.max_domain_aliases || 3,
    );

    watch(
      () => formSystem.meta.value.valid,
      (valid, wasValid) => {
        // Silent mount validation flags invalid persisted data (e.g. a legacy
        // empty domain) without rendering errors; surface them once, while the
        // form is still untouched.
        if (!valid && wasValid && !formSystem.meta.value.dirty) void formSystem.validate();
        emit("update:formValid", valid);
      },
      { immediate: true },
    );

    return {
      appsStore,
      formSystem,
      userStore: useUserStore(),
      toastsStore: useToastsStore(),
      compositeImageOptions: [
        { text: "JPEG", value: "image/jpeg" },
        { text: "PNG", value: "image/png" },
        { text: "WebP", value: "image/webp" },
      ],
      compositeImageQualityOptions: [
        { text: "60", value: 60 },
        { text: "65", value: 65 },
        { text: "70", value: 70 },
        { text: "75", value: 75 },
        { text: "80", value: 80 },
        { text: "85", value: 85 },
        { text: "90", value: 90 },
        { text: "95", value: 95 },
        { text: "100", value: 100 },
      ],
    };
  },
  data() {
    return {
      showApiDialog: false,
      name: this.model.name,
      domain: this.model.domain,
      domain_aliases: this.arrayToString(this.model.domain_aliases),
      timezone_offset: this.model.timezone_offset,
      session_timeout: this.model.flags.session_timeout,
      composite_img_mimetype: this.model.flags.composite_img_mimetype,
      composite_img_quality: this.model.flags.composite_img_quality,
      fromEmail: this.model.pinpoint_sender_email,
      emailDialog: false,
      copyToClipboard,
    };
  },
  computed: {
    pinpointEnabled() {
      if (!this.model.flags.email_path_enabled) return false;
      if (this.model && this.model.providers) {
        const { providers } = this.model;
        return !!(providers.redfast && providers.redfast.pinpoint_app_id);
      }
      return false;
    },
    emailSubmitDisabled() {
      return !this.fromEmail;
    },
    currApp() {
      return this.appsStore.app;
    },
    timezoneOptions() {
      return Object.entries(this.model.timezones).map(([key, value]) => {
        return { text: key, value: value };
      });
    },
  },
  watch: {
    currApp(to) {
      if (to) {
        this.fromEmail = to.pinpoint_sender_email;
      }
    },
    composite_img_mimetype(to) {
      if (to === "image/png") {
        this.composite_img_quality = 100;
      }
    },
  },
  methods: {
    showApiKeyDialog() {
      this.showApiDialog = true;
    },
    closeApiKeyDialog() {
      this.showApiDialog = false;
    },
    regenerateApiKey() {
      this.appsStore.generateApiKey(this.model.id);
      this.showApiDialog = false;
    },
    arrayToString(arr) {
      if (!arr) {
        return "";
      }

      return arr.join(", ");
    },
    fillModel(model) {
      // Use formSystem values for name and domain, trimmed for the API
      model.name = this.formSystem.values.name.value.trim();
      model.domain = this.formSystem.values.domain.value.trim();

      for (const prop of ["timezone_offset", "filter"]) {
        model[prop] = this[prop];
      }
      model.flags.session_timeout = this.session_timeout;
      model.flags.composite_img_mimetype = this.composite_img_mimetype;
      model.flags.composite_img_quality = this.composite_img_quality;

      // Use formSystem.values.domainList which is what the input modifies
      if (
        this.formSystem.values.domainList.value &&
        this.formSystem.values.domainList.value.length > 0
      ) {
        const parsedAliases = this.formSystem.values.domainList.value.reduce((acc, site) => {
          const trimmed = site.trim();
          if (trimmed) {
            acc.push(trimmed.replace(/(^\w+:|^)\/\//, ""));
          }
          return acc;
        }, []);
        model.domain_aliases = parsedAliases;
      } else {
        model.domain_aliases = [];
      }
    },
    async validateForm() {
      const { valid } = await this.formSystem.validate();
      return valid;
    },
    showEmailDialog() {
      this.emailDialog = true;
    },
    closeEmailDialog() {
      this.emailDialog = false;
    },
    async submitEmail() {
      await this.appsStore.verifyPinpointEmail({ appId: this.model.id, email: this.fromEmail });
      this.fromEmail = this.model.pinpoint_sender_email;
      this.closeEmailDialog();
      this.toastsStore.create({
        type: "success",
        body: "Thank you. Expect an email from Amazon SES with further instructions",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.system-app-content {
  padding: 30px 30px 0px;
  gap: 20px 48px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(max(400px, 45%), 1fr));
  grid-template-rows: min-content;
}
</style>
